import { Action, FlipType, SaveFormat, SaveOptions, manipulateAsync } from 'expo-image-manipulator';

import { Photo, PhotoFormat } from './types';

const BYTE_TO_MB = 1024 * 1024;

export const isAboveMax = (maxSize: number, currentSize: number) => currentSize > maxSize;

export const isValidSize = (value: string, maxSize: number): boolean => {
  const currentValueInMb = getSizeInMb(value);
  return !isAboveMax(maxSize, currentValueInMb);
};

export const getSizeInMb = (value: string) => {
  const bytes = getBase64Bytes(value);
  return bytes / BYTE_TO_MB;
};

export const getBase64Bytes = (base64: string) => {
  // Remove any data url scheme
  const base64Hash = base64.split(',')[1] || base64;
  // This refers to the extra = or == characters at the end of a base64 string
  const padding = (base64.match(/=+$/) || [''])[0].length;
  // Base64 encodes every 3 bytes of binary data into 4 characters.
  // So, to get the number of groups of 3 bytes in your base64 string.
  // Each group of 3 bytes is represented by 4 Base64 characters.
  // The total number of Base64 characters
  return (base64Hash.length * 3) / 4 - padding;
};

export const isLandascape = (width: number, height: number) => width > height;

export const getFormatFromUri = (uri: string = '') => {
  const match = uri.match(/\.(\w+)(\?.*)?$/i);
  if (!match) {
    return null;
  }

  let ext = match[1].toLowerCase();
  if (['jpg', 'jpeg'].includes(ext)) {
    ext = PhotoFormat.JPEG;
  }

  return ext as PhotoFormat;
};

export const getFormatFromBase64 = (base64String: string = '') => {
  // Check for data URL prefix
  const match = base64String.match(/^data:image\/(\w+);base64,/i);
  return match ? (match[1].toLowerCase() as PhotoFormat) : null;
};

export const getPhotoManipulationConfig = (
  photo: Photo,
  flip: string,
  format?: PhotoFormat,
  maxSize?: number,
  maxWidth?: number,
  maxHeight?: number,
  fixMirroring?: boolean
) => {
  const configuration: Action[] = [];
  const saveOption: SaveOptions = {};

  const currentFormat = getFormatFromUri(photo.uri) || getFormatFromBase64(photo.base64);

  // Resizing is to expensive on android, so ideally we should respect the aspect/ratio of the image
  // We are assuming that all photos are in portrait mode, not landscape
  if (
    (photo?.width && maxWidth && isAboveMax(maxWidth, photo.width)) ||
    (photo?.height && maxHeight && isAboveMax(maxHeight, photo.height))
  ) {
    configuration.push({ resize: { width: maxWidth, height: maxHeight } });
  }

  if (flip === 'front' && fixMirroring) {
    configuration.push({ rotate: 180 }, { flip: FlipType.Vertical });
  }

  saveOption.format = ((format && format !== currentFormat
    ? format
    : currentFormat) as unknown) as SaveFormat;

  if (maxSize && photo?.base64 && !isValidSize(photo?.base64, maxSize)) {
    saveOption.compress = 0.8;
  }

  if (saveOption.format || saveOption.compress) {
    saveOption.base64 = true;
  }

  return { configuration, saveOption };
};

export const processPhoto = async (
  photo: Photo,
  flip: string,
  format?: PhotoFormat,
  maxSize?: number,
  maxWidth?: number,
  maxHeight?: number,
  fixMirroring?: boolean
) => {
  let photoBase64 = photo.base64 ?? '';
  const { configuration, saveOption } = getPhotoManipulationConfig(
    photo,
    flip,
    format,
    maxSize,
    maxWidth,
    maxHeight,
    fixMirroring
  );

  if (photo?.uri && (configuration.length || saveOption.base64)) {
    const img = await manipulateAsync(photo.uri, configuration, saveOption);
    photoBase64 = img.base64 || photoBase64;
  }
  return photoBase64;
};

export const getNormalizedImage = (image: string): string => {
  if (image.startsWith('data:image/') || image.startsWith('https://')) {
    return image;
  }
  return `data:image/png;base64,${image}`;
};
